/** Session completion screen — shows score ring, pass/fail status, and next-action buttons. */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from '@/interfaces/session-player/ui';
import {
  ArrowRight,
  RotateCcw,
  ClipboardList,
  BookOpen,
  ArrowLeft,
  X as XIcon,
  Loader2,
} from 'lucide-react';
import type { SessionMode, CompletionMetrics } from './types';
import { useSession } from './SessionContext';
import { AnswerReviewScreen } from './AnswerReviewScreen';
import { ScoreRing } from './components/ScoreRing';
import { CompletionCheckmark } from './components/CompletionCheckmark';
import { SessionFeedback } from './components/SessionFeedback';

const EASE = [0.16, 1, 0.3, 1] as const;

// ─── Types ──────────────────────────────────────────────────────────────────

interface SessionCompletionScreenProps {
  metrics: CompletionMetrics;
  mode: SessionMode;
  playComplete?: () => void;
  onExit: () => void;
  onNextLesson?: () => void;
  onRetry?: () => void;
  onReviseLesson?: () => void;
  revisingLesson?: boolean;
  isPersonalLesson?: boolean;
  /** Pass percentage for the lesson (null = no pass mark, always passes). */
  passPct?: number | null;
}

// ─── Score screen (inner) ───────────────────────────────────────────────────

/**
 * Format active learning minutes for the completion stat strip. Round-UP with
 * floor of 1 once any positive time exists (matches the in-session indicator).
 * Returns null when there's nothing meaningful to show — caller hides the line.
 */
function formatActiveLearningMinutes(activeMinutesInSession: number | null | undefined): string | null {
  if (activeMinutesInSession == null || activeMinutesInSession <= 0) return null;
  const minutes = Math.max(1, Math.ceil(activeMinutesInSession));
  return `${minutes} min of active learning`;
}

function ScoreScreen({
  metrics,
  mode,
  passed,
  passPct,
  isPersonalLesson,
  sessionId,
  activeLearningLabel,
  submittedRating,
  feedbackSent,
  onRated,
  onRatingFailed,
  onFeedbackSent,
  onExit,
  onNextLesson,
  onRetry,
  onReviseLesson,
  revisingLesson,
  onReview,
}: {
  metrics: CompletionMetrics;
  mode: SessionMode;
  passed: boolean;
  passPct: number | null | undefined;
  isPersonalLesson?: boolean;
  sessionId: string;
  activeLearningLabel: string | null;
  submittedRating: number | null;
  feedbackSent: boolean;
  onRated: (rating: number) => void;
  onRatingFailed: () => void;
  onFeedbackSent: () => void;
  onExit: () => void;
  onNextLesson?: () => void;
  onRetry?: () => void;
  onReviseLesson?: () => void;
  revisingLesson?: boolean;
  onReview: () => void;
}) {
  const modeLabel = mode === 'learn' ? 'Lesson' : 'Revision';
  const isLearnMode = mode === 'learn';
  const showRetry = isLearnMode && !passed && !!onRetry;
  const showNext = isLearnMode && passed && !!onNextLesson;
  const hasPassMark = isLearnMode && passPct != null && metrics.totalMaxScore > 0;
  const isInfoOnly = metrics.totalMaxScore === 0;

  // Exit label depends on context
  const exitLabel = mode === 'revise' || isPersonalLesson ? 'Close' : 'Back to course';
  const ExitIcon = mode === 'revise' || isPersonalLesson ? XIcon : ArrowLeft;

  // Show exit as secondary when another action is primary
  const exitIsPrimary = !showNext && !showRetry;

  return (
    <motion.div
      className="flex min-h-dvh flex-col items-center justify-center px-6 py-12"
      style={{ background: 'var(--f-bg)' }}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4, ease: EASE }}
    >
      {/* Heading */}
      <motion.h1
        className="mb-8 font-semibold"
        style={{
          fontSize: 'var(--f-text-2xl)',
          fontFamily: 'var(--f-font-display)',
          color: 'var(--f-fg)',
          letterSpacing: 'var(--f-tracking-tight)',
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 24, delay: 0.1 }}
      >
        {modeLabel} complete
      </motion.h1>

      {/* Score ring or completion checkmark */}
      {isInfoOnly ? <CompletionCheckmark /> : <ScoreRing scorePct={metrics.scorePct} />}

      {/* Pass mark indicator */}
      {hasPassMark && (
        <motion.p
          className="mt-1"
          style={{
            fontSize: 'var(--f-text-sm)',
            color: passed ? 'var(--f-success-text)' : 'var(--f-error-text)',
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.85 }}
        >
          {passed
            ? `Pass mark: ${passPct}% — passed!`
            : `Pass mark: ${passPct}% — ${Math.ceil(passPct! - metrics.scorePct)}% more needed`}
        </motion.p>
      )}

      {/* Steps completed */}
      <motion.p
        className="mt-1"
        style={{ fontSize: 'var(--f-text-sm)', color: 'var(--f-fg-muted)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.9 }}
      >
        {metrics.stepsCompleted} step{metrics.stepsCompleted !== 1 ? 's' : ''} completed
      </motion.p>

      {/* Active learning time (algo v1.8.0) — session-scoped, from step_time sums. */}
      {activeLearningLabel && (
        <motion.p
          className="mt-1"
          style={{ fontSize: 'var(--f-text-sm)', color: 'var(--f-fg-muted)' }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.95 }}
        >
          {activeLearningLabel}
        </motion.p>
      )}

      {/* Session feedback */}
      <div className="mt-6">
        <SessionFeedback
          sessionId={sessionId}
          submittedRating={submittedRating}
          feedbackSent={feedbackSent}
          onRated={onRated}
          onRatingFailed={onRatingFailed}
          onFeedbackSent={onFeedbackSent}
        />
      </div>

      {/* Actions */}
      <motion.div
        className="mt-6 flex w-full max-w-xs flex-col gap-3"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1, duration: 0.4 }}
      >
        {/* Primary: Continue / Retry / Exit */}
        {showNext && (
          <Button
            variant="ghost"
            onPress={onNextLesson}
            className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'var(--f-accent)',
              color: 'var(--f-fg-on-accent)',
              boxShadow: 'var(--f-shadow-accent)',
              fontFamily: 'var(--f-font-display)',
            }}
          >
            Continue to next lesson
            <ArrowRight size={18} />
          </Button>
        )}

        {showRetry && (
          <Button
            variant="ghost"
            onPress={onRetry}
            className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'var(--f-accent)',
              color: 'var(--f-fg-on-accent)',
              boxShadow: 'var(--f-shadow-accent)',
              fontFamily: 'var(--f-font-display)',
            }}
          >
            <RotateCcw size={18} />
            Retry
          </Button>
        )}

        {exitIsPrimary && (
          <Button
            variant="ghost"
            onPress={onExit}
            className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'var(--f-accent)',
              color: 'var(--f-fg-on-accent)',
              boxShadow: 'var(--f-shadow-accent)',
              fontFamily: 'var(--f-font-display)',
            }}
          >
            <ExitIcon size={18} />
            {exitLabel}
          </Button>
        )}

        {/* Review Answers — always shown */}
        <Button
          variant="ghost"
          onPress={onReview}
          className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: 'var(--f-bg-sunken)',
            color: 'var(--f-fg)',
            fontFamily: 'var(--f-font-display)',
          }}
        >
          <ClipboardList size={18} />
          {isInfoOnly ? 'Review content' : 'Review Answers'}
        </Button>

        {/* Revise this — conditional */}
        {onReviseLesson && !isInfoOnly && (
          <Button
            variant="ghost"
            onPress={onReviseLesson}
            isDisabled={revisingLesson}
            className="flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold transition-all hover:scale-[1.02] active:scale-[0.98]"
            style={{
              background: 'var(--f-bg-sunken)',
              color: 'var(--f-fg)',
              fontFamily: 'var(--f-font-display)',
              opacity: revisingLesson ? 0.6 : 1,
            }}
          >
            {revisingLesson ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <BookOpen size={18} />
            )}
            {revisingLesson ? 'Starting revision…' : 'Revise this'}
          </Button>
        )}

        {/* Exit as secondary (when not primary) */}
        {!exitIsPrimary && (
          <Button
            variant="ghost"
            onPress={onExit}
            className="flex items-center justify-center gap-2 rounded-xl px-6 py-3 font-medium transition-opacity hover:opacity-80"
            style={{ color: 'var(--f-fg-muted)' }}
          >
            <ExitIcon size={18} />
            {exitLabel}
          </Button>
        )}
      </motion.div>
    </motion.div>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────

export function SessionCompletionScreen({
  metrics,
  mode,
  playComplete,
  onExit,
  onNextLesson,
  onRetry,
  onReviseLesson,
  revisingLesson,
  isPersonalLesson,
  passPct,
}: SessionCompletionScreenProps) {
  const { state } = useSession();
  const hasPlayedRef = useRef(false);
  const [showReview, setShowReview] = useState(false);
  const [submittedRating, setSubmittedRating] = useState<number | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);

  // Derive pass/fail from metrics and passPct
  const passed = passPct != null ? metrics.scorePct >= passPct : true;

  // Active learning time for THIS lesson (session-scoped). Comes from the last
  // step-submit response (algo v1.8.0 wires `activeMinutesInSession` into the
  // readiness payload). Null until the first submit has landed; shown as
  // "X min of active learning" on the stat strip below the step count.
  const activeLearningLabel = formatActiveLearningMinutes(
    state.readiness?.activeMinutesInSession
  );

  useEffect(() => {
    if (state.soundEnabled && playComplete && passed && !hasPlayedRef.current) {
      hasPlayedRef.current = true;
      playComplete();
    }
  }, [state.soundEnabled, playComplete, passed]);

  return (
    <AnimatePresence mode="wait">
      {showReview ? (
        <AnswerReviewScreen
          key="review"
          steps={state.steps}
          answeredSteps={state.answeredSteps}
          isPersonalLesson={isPersonalLesson}
          onBack={() => setShowReview(false)}
        />
      ) : (
        <ScoreScreen
          key="score"
          metrics={metrics}
          mode={mode}
          passed={passed}
          passPct={passPct}
          isPersonalLesson={isPersonalLesson}
          sessionId={state.sessionId}
          activeLearningLabel={activeLearningLabel}
          submittedRating={submittedRating}
          feedbackSent={feedbackSent}
          onRated={setSubmittedRating}
          onRatingFailed={() => setSubmittedRating(null)}
          onFeedbackSent={() => setFeedbackSent(true)}
          onExit={onExit}
          onNextLesson={onNextLesson}
          onRetry={onRetry}
          onReviseLesson={onReviseLesson}
          revisingLesson={revisingLesson}
          onReview={() => setShowReview(true)}
        />
      )}
    </AnimatePresence>
  );
}
